import spartaEventsUtility from "utilities/global/sparta-events-utility/2.3.5/js/sparta-events-utility";
import spartaGlobalFunctions from "utilities/global/sparta-global-functions-utility/2.1.3/js/sparta-global-functions-utility";
import spartaMediaQueryUtility from "utilities/global/sparta-mediaquery-utility/2.0.4/js/sparta-mediaquery-utility";
import spartaStoreUtility from "utilities/global/sparta-store-utility/3.1.1/js/sparta-store-utility";
import spartaStyleUtility from "utilities/global/sparta-style-utility/3.2.4/js/sparta-style-utility";
import jQuery from "utilities/vendor/jquery/3.5.1/js/jquery";
import $ from "utilities/vendor/jquery/3.5.1/js/jquery";
import spartaValidationFramework from "utilities/global/sparta-validation-framework/7.1.7/js/sparta-validation-framework";
import spartaInputErrorUtility from "utilities/global/sparta-input-error-utility/7.2.3/js/sparta-input-error-utility";
import spartaInputFormattingUtility from "utilities/global/sparta-input-formatting-utility/2.1.1/js/sparta-input-formatting-utility";
import spartaMaskingUtility from "utilities/global/sparta-masking-utility/5.3.0/js/sparta-masking-utility";
import DataAssetLoader from "utilities/suits/data-asset-loader/2.1.1/js/data-asset-loader";
/* global DataAssetLoader spartaEventsUtility spartaInputFormattingUtility spartaMaskingUtility spartaStoreUtility spartaValidationFramework */

/**
 * @class SpartaInputUtility
 * @description
 *  configure inputs for validation, formatting, obfuscation, etc.
 */
class SpartaInputUtility {
  /**
   * @method constructor
   * @param {String} selector query selector string for the form that we will be initializing
   * @param {Object} [settings={}] Optional Object for configuring overrides
   * @description initializes new instance of SpartaInputUtility Class
   */
  constructor(selector, settings = {}) {
    // get mobile browser setting from Store
    this.isMobile = spartaStoreUtility.getData('browser').mobile;

    // extend options with provided settings
    this.options = Object.assign({}, settings);

    this.inputs = [];
    this.optionInputs = [];

    this.counterOffset = 11;

    /*
      NOTE: to maximize compatibility with previous versions of this utility
      we accept either a query selector string, jQuery Object, or HTML element
      for the selector parameter, but a simple query string is preferred
    */

    // set instance scope based on selector argument
    // check to see if we were given an HTML Node
    if (!selector.nodeName) {
      // check to see if we were given a jQuery Object
      if (!selector[0]) {
        this.scope = document.querySelector(selector);
      } else {
        this.scope = selector[0];
      }
    } else {
      this.scope = selector;
    }

    // initialize the validation framework
    spartaValidationFramework.init();

    // initialize instance
    this.init();
  }

  /**
   * @method init
   * @description initialize utility on desired inputs
   */
  init() {
    // get list of inputs
    this.getInputs();

    // get list of options
    this.getOptions();

    // get list of input help buttons
    this.getTriggers();

    // check for masking option overrides
    this.maskOptions = Object.assign({}, this.options.inputMaskOptionsExtend);

    // set character limits on text areas if defined
    this.getTextAreas();

    if (!this.importTriggered) {
      this.loadInputStrings().then((xhr) => {
        this.inputText = xhr.inputs;
        this.importTriggered = true;

        this.setHelpBttnText();
      });
    } else {
      this.setHelpBttnText();
    }

    // start up
    this.boot();
  }

  /**
   * @method getInputs
   * @description find inputs within scope and return set this.inputs to that Array
   */
  getInputs() {
    // cache all inputs in case we need to iterate through them later
    this.inputs = [].slice.call(this.scope.getElementsByClassName('spa-input-box'));
  }

  /**
   * @method getOptions
   * @description find options within scope and return set this.options to that Array
   */
  getOptions() {
    // cache all options in case we need to iterate through them later
    this.optionInputs = [].slice.call(this.scope.getElementsByClassName('spa-input-options-wrapper'));
  }

  /**
   * @method getTriggers
   * @description find input help buttons within scope and return set this.triggers to that Array
   */
  getTriggers() {
    this.triggers = [].slice.call(this.scope.getElementsByClassName('toggle-trigger')).concat(
      [].slice.call(this.scope.getElementsByClassName('close-trigger'))
    );
  }

  /**
   * @method getTextAreas
   * @description iterates over text area inputs and calls counter function
   * for those that contain .spar-char-count
   */
  getTextAreas() {
    this.inputs.filter((input) => {
      return input.classList.contains('spa-input-box--textarea')
               && input.querySelector('.spa-char-count');
    }).forEach((input) => {
      this.setCharCounter(input);
    });
  }

  /**
   * @method setHelpBttnText
   * @description sets ADA text for help buttons
   */
  setHelpBttnText() {
    const inputText = this.inputText;

    this.triggers.forEach((trigger) => {
      const label = trigger.getAttribute('aria-label');

      if (!label.includes(inputText.openHelp) && !label.includes(inputText.closeHelp)) {
        if (trigger.classList.contains('toggle-trigger')) {
          trigger.setAttribute('aria-label', inputText.openHelp.concat(label));
        } else if (trigger.classList.contains('close-trigger')) {
          trigger.setAttribute('aria-label', inputText.closeHelp.concat(label));
        }
      }
    });
  }

  /**
   * @method loadInputStrings
   * @description load language specific input strings
   */
  loadInputStrings() {
    this.language = spartaStoreUtility.getData('lang').split('-')[0];

    // generate a new DataAssetLoader instance
    const messagingAssetLoader = new DataAssetLoader('global/sparta-input-utility', `data/${this.language}`);

    // load the relevant messages JSON file, which triggers success and failure events so that the utility can subscribe to them
    const load = messagingAssetLoader.loadAsset('inputs.json');

    // generate a promise with the results from our event subscription so we can
    // return the name of the file that was actually loaded
    // this serves mostly as an example of how to deal with this
    const assetLoaderPromise = new Promise((resolve) => {
      // subscribe to the event emitted by the DataAssetLoader
      // we use true for the retroactive parameter to make sure that even if we miss
      // the initial event firing due to it happening quicker than expected, we can
      // still respond to it
      spartaEventsUtility.sub(load.success, (evtData) => {
        // define the messageList object from the messages.json file
        resolve(evtData.data.xhr);
      }, true);
    });

    spartaEventsUtility.sub(load.failure, (evtData) => {
      // log out an error for debugging the issue
      if (window.console && console.warn) {
        console.warn(`Sparta Inputs: ${evtData.data.filename} AJAX Error.`, evtData.data.xhr);
      }
    }, true);

    return assetLoaderPromise;
  }

  /**
   * @method boot
   * @description set up formatting, conditional content, and any device-specific field options
   */
  boot() {
    // configure fields with conditional content
    this.conditionalFields();

    // configure any device-specific input types
    this.changeInputTypes();

    // Formatting
    // initialize the sparta-input-formatting-utility
    if (spartaInputFormattingUtility) {
      spartaInputFormattingUtility.init();
    }

    // GIS Obfuscation
    // initialize the sparta-masking-utility
    spartaMaskingUtility.init();
  }

  /**
   * @method conditionalFields
   * @description iterate through inputs, find ones with conditional content, inject content
   */
  conditionalFields() {
    // iterate through inputs
    this.optionInputs.forEach((container) => {
      // if we have conditional content
      if (container.hasAttribute('data-option-has-conditional-content')) {
        // create temporary DOM fragment to store conditional content
        const conditionalContentFragment = document.createDocumentFragment();

        // get conditional content element's ID
        const conditionalContentContainerString = container.getAttribute('data-conditional-content');

        // get conditional content DOM node
        const conditionalContentOriginalContainer = document.getElementById(conditionalContentContainerString);

        // Skip this container if the specifified conditional content is not actually on the page.
        if (!conditionalContentOriginalContainer) {
          return;
        }

        conditionalContentOriginalContainer.classList.add('spa-input-conditional-content-wrapper');
        conditionalContentOriginalContainer.classList.remove('spa-input-conditional-content-container');

        // append conditional content DOM node to temporary DOM fragment
        conditionalContentFragment.appendChild(conditionalContentOriginalContainer);

        // check to see if conditional content container exists
        let conditionalContentNewContainer = container.querySelector('data-option-conditional-content');

        // if conditional content container does not exist, create it
        if (!conditionalContentNewContainer) {
          // create element
          conditionalContentNewContainer = document.createElement('div');

          // add attributes
          conditionalContentNewContainer.setAttribute('data-option-conditional-content', true);
          conditionalContentNewContainer.classList.add('spa-input-option-conditional-content');

          // append element to our container
          container.appendChild(conditionalContentNewContainer);
        }

        // inject DOM fragment into conditional content container
        conditionalContentNewContainer.appendChild(conditionalContentFragment);
      }
    });
  }

  /**
   * @method changeInputTypes
   * @description we want to use the native HTML 5 input types for some mobile inputs,
   *  but these inputs can have undesireable effects in some non-mobile browsers,
   *  because of this we only want to swap types dynamically at runtime
   */
  changeInputTypes() {
    // iterate through inputs
    this.inputs.forEach((container) => {
      // check to see if input has a dynamic mobile type or pattern
      const input = container.querySelector('[data-mobile-input-type], [data-mobile-pattern]');

      // if we have device-specific types
      if (input) {
        // initialize variables for holding our type and pattern
        // these are currently the only two attributes we change based on device
        let type;
        let pattern;

        // get device-specific type and pattern
        if (this.isMobile) {
          type = input.getAttribute('data-mobile-input-type');
          pattern = input.getAttribute('data-mobile-pattern');
        } else {
          type = input.getAttribute('data-default-input-type');
          pattern = input.getAttribute('data-default-pattern');
        }

        // set new type if it is different, check to make sure type exists
        if (type && input.getAttribute('type') !== type) {
          input.setAttribute('type', type);
        }

        // set new pattern if it is different
        if (input.getAttribute('pattern') !== pattern) {
          input.setAttribute('pattern', pattern);
        }
      }
    });
  }

  /**
   * @method setCharCounter
   * @param {Object} elementObj container of text area that character count is applied to
   * @description initializes character counter for sparta text areas
   */
  setCharCounter(elementObj) {
    if (typeof elementObj !== 'undefined' && elementObj) {
      const charStore = this.charStore = {};
      const el = charStore.el = elementObj.querySelector('.spa-input-textarea');
      const countNode = charStore.countNode = elementObj.querySelector('.spa-char-count');
      const areaPosition = el.getBoundingClientRect();

      charStore.maxLength = countNode.dataset.charLimit || 100;
      if (el.parentNode.classList.contains('has-limit')) {
        countNode.style.top = `${areaPosition.height - 1}px`;
      } else {
        countNode.style.top = `${areaPosition.height + this.counterOffset}px`;
      }
      countNode.classList.add('spa-char-count--loaded');

      el.addEventListener('keypress', this.updateCharCounter.bind(this));
      el.addEventListener('keyup', this.updateCharCounter.bind(this));
    }
  }

  /**
   * @method updateCharCounter
   * @param {Object} event event that is emitted from eventListener
   * @description updates character count in text area
   */
  updateCharCounter(event) {
    // Update current character count on keyup
    const charStore = this.charStore;
    const maxLength = charStore.maxLength;
    const el = charStore.el;
    const countNode = charStore.countNode;

    if (el.scrollHeight > el.clientHeight) {
      countNode.classList.add('spa-char-count--scrollbar');
    } else if (el.scrollHeight <= el.clientHeight && countNode.classList.contains('scrollbar')) {
      countNode.classList.remove('scrollbar');
    }

    //enter or backspace should not be counted
    if (event.keyCode === 13 || event.keyCode === 8) { return false; }

    countNode.innerHTML = `${maxLength - parseInt(el.value.length, 10)} characters remaining`;
    if (el.value.length == maxLength) {
      event.preventDefault();
      return false;
    } else if (el.value.length > maxLength) {
      event.preventDefault();
      el.value = el.value.substring(0, maxLength);
    }
    return undefined;
  }
}

$.fn.bindHelp = function bindHelp() {
  const $elements = $(this);
  const namespace = 'inputHelp';

  return $elements.each(function iterateThroughElements() {
    const $trigger = $(this);
    const sInputId = $trigger.attr("id").replace('help-open-', '');
    const $targetHelpSection = $(`#helpLayer_${sInputId}`);
    const $targetHelpClose = $(`#help-close-${sInputId}`);

    // Setting up event binds...
    $trigger.off(`click.${namespace}`);

    $trigger.on(`click.${namespace}`, () => {
      $targetHelpSection.slideToggle(150, () => {
        if ($targetHelpSection.is(':visible')) {
          $targetHelpSection.attr('aria-hidden', 'false');

          $targetHelpClose.focus();
        } else {
          $targetHelpSection.attr('aria-hidden', 'true');
        }
      });
    });

    $targetHelpClose.off(`click.${namespace}`);

    $targetHelpClose.on(`click.${namespace}`, () => {
      $targetHelpSection.slideUp(150);
      $trigger.focus();
    });
  });
};

$.fn.disableCopyAndPaste = function disableCopyAndPaste() {
  const namespace = 'disableCopyAndPaste';

  $(this).on(`cut.${namespace} copy.${namespace} paste.${namespace}`, (e) => {
    e.preventDefault();
  });
};

// export our jQuery plugin
export default $.fn.spartaInputs = function spartaInputs(settings = {}) {
  // initialize variable for storing selector
  let inputs;

  // trigger input help
  $(this).find('.toggle-trigger, .toggle-trigger-link').bindHelp();

  // gis requirement, disable copy and paste for ssn fields
  $(this).find('[data-sparta-input-format=ssn]').disableCopyAndPaste();

  // return each element in the jQuery collection to allow jQuery method chaining
  return this.each(() => {
    inputs = new SpartaInputUtility($(this), settings);
  });
};
